import React, { useEffect, useState } from "react";
import IbPending from "./components/IbPending";
import IbAccepted from "./components/IbAccepted";
import IbReject from "./components/IbReject";
import { useSearchParams } from "react-router-dom";
import { DatePicker, Form, Input, Select } from "antd";
import Segmented from "../../components/segmented/Segmented.jsx";
import {
  GetAllibApi,
  GetIBUserListApi,
  ExportIBHierarchyCommissionLotsApi,
  ExportIBDealCommissionDistributionApi,
  GetIbParentHierarchyApi,
} from "../../api/request/ibapproved.js";
import ExportHandler from "../../components/global/export/ExportHandler.jsx";
import ModalComponent from "../../components/global/modal/ModalComponent.jsx";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import { useUserStore } from "../../store/userStore.js";
import toast from "react-hot-toast";
import { getDownloadFilenameFromResponse, getIBHierarchyReportFallbackName, getIBDealCommissionReportFallbackName } from "../../utils/utils.js";

const { RangePicker } = DatePicker;

export default function IbApproved() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState("Pending");
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDealReportModalOpen, setIsDealReportModalOpen] = useState(false);
  const [ibEmailOptions, setIbEmailOptions] = useState([]);
  const [isIbOptionsLoading, setIsIbOptionsLoading] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [isDealReportLoading, setIsDealReportLoading] = useState(false);
  const [parentIbOptions, setParentIbOptions] = useState([]);
  const [isParentOptionsLoading, setIsParentOptionsLoading] = useState(false);
  const [selectedIbIsMaster, setSelectedIbIsMaster] = useState(false);
  const [reportForm] = Form.useForm();
  const [dealReportForm] = Form.useForm();
  const userDetails = useUserStore((state) => state.userDetails);
  const isAdmin =
    userDetails?.roleName?.toLowerCase() === "admin" || userDetails?.roleId == 1;

  const exportHandler = async () => {
    try {
      let params = {
        ApplyPagination: false,
        SearchText: searchText,
        FilterText:
          value === "Pending"
            ? "Pending"
            : value === "Approved"
              ? "Approved"
              : value === "Declined"
                ? "Rejected"
                : value,
        SortBy: "actionDate",
      };
      const { data } = await GetAllibApi(
        new URLSearchParams(params).toString(),
      );
      if (data?.success) {
        return {
          data: data?.data,
          removableColumns: ["userId"],
          fileName: "IBs",
        };
      } else {
        return { data: [], removableColumns: [] };
      }
    } catch (error) {
      return { data: [], removableColumns: [] };
    }
  };

  const getIbEmailOptions = async () => {
    setIsIbOptionsLoading(true);
    try {
      const params = {
        UserType: "ib",
        UserId: userDetails?.userId,
      };
      const { data } = await GetIBUserListApi(
        new URLSearchParams(params).toString(),
      );
      if (data?.success) {
        setIbEmailOptions(
          (data?.data || [])
            .filter((item) => item?.email)
            .map((item) => ({
              label: `${item?.name || "--"} (${item.email})`,
              value: item.email,
            })),
        );
      } else {
        setIbEmailOptions([]);
      }
    } catch (error) {
      setIbEmailOptions([]);
    } finally {
      setIsIbOptionsLoading(false);
    }
  };

  const handleOpenReportModal = () => {
    reportForm.resetFields();
    setIsReportModalOpen(true);
    getIbEmailOptions();
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
    reportForm.resetFields();
  };

  const resetParentIbOptions = () => {
    setParentIbOptions([]);
    setSelectedIbIsMaster(false);
    setIsParentOptionsLoading(false);
  };

  const handleOpenDealReportModal = () => {
    dealReportForm.resetFields();
    resetParentIbOptions();
    setIsDealReportModalOpen(true);
    getIbEmailOptions();
  };

  const handleCloseDealReportModal = () => {
    setIsDealReportModalOpen(false);
    dealReportForm.resetFields();
    resetParentIbOptions();
  };

  const handleIbEmailChange = async (email) => {
    dealReportForm.setFieldValue("parentEmail", undefined);
    setParentIbOptions([]);
    setSelectedIbIsMaster(false);
    if (!email) return;

    setIsParentOptionsLoading(true);
    try {
      const { data } = await GetIbParentHierarchyApi(email);
      if (!data?.success) {
        toast.error(data?.message || "Failed to load parent IBs.");
        return;
      }

      const parents = (data?.data || [])
        .filter((item) => item?.email && Number(item.level) > 0)
        .sort((a, b) => Number(a.level) - Number(b.level));
      const topLevel = parents.reduce(
        (highest, item) => Math.max(highest, Number(item.level) || 0),
        0,
      );

      setSelectedIbIsMaster(parents.length === 0);
      setParentIbOptions(
        parents.map((item) => {
          const isMaster = Number(item.level) === topLevel;
          const prefix = isMaster ? "Master IB" : `Parent ${item.level}`;
          return {
            label: `${prefix} — ${item.name || "--"} (${item.email})`,
            value: item.email,
          };
        }),
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to load parent IBs.",
      );
    } finally {
      setIsParentOptionsLoading(false);
    }
  };

  const downloadBlobFile = (blobData, fileName) => {
    const blob = new Blob([blobData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const handleGetReport = async (values) => {
    try {
      setIsReportLoading(true);
      const [from, to] = values?.dateRange || [];
      const params = {
        email: values?.email,
        fromDate: from ? from.format("YYYY-MM-DD") : "",
        toDate: to ? to.format("YYYY-MM-DD") : "",
      };

      const response = await ExportIBHierarchyCommissionLotsApi(
        new URLSearchParams(params).toString(),
      );

      const contentType = response?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        toast.error(parsed?.message || parsed?.error?.message || "Failed to get report.");
        return;
      }

      downloadBlobFile(
        response.data,
        getDownloadFilenameFromResponse(
          response,
          getIBHierarchyReportFallbackName({ email: params.email }),
        ),
      );
      toast.success("Report downloaded successfully.");
      handleCloseReportModal();
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to get report.",
      );
    } finally {
      setIsReportLoading(false);
    }
  };

  const readReportError = async (error) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        return parsed?.error?.message || parsed?.error?.Message || parsed?.message;
      } catch (parseError) {
        return null;
      }
    }
    return data?.error?.message || data?.error?.Message || data?.message;
  };

  const handleGetDealReport = async (values) => {
    try {
      setIsDealReportLoading(true);
      const params = {
        ibEmail: values?.ibEmail,
      };
      if (values?.parentEmail) {
        params.parentEmail = values.parentEmail;
      }

      const response = await ExportIBDealCommissionDistributionApi(
        new URLSearchParams(params).toString(),
      );

      const contentType = response?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        toast.error(parsed?.error?.message || parsed?.error?.Message || parsed?.message || "Failed to get report.");
        return;
      }

      downloadBlobFile(
        response.data,
        getDownloadFilenameFromResponse(
          response,
          getIBDealCommissionReportFallbackName({ email: params.ibEmail }),
        ),
      );
      toast.success("Report downloaded successfully.");
      handleCloseDealReportModal();
    } catch (error) {
      toast.error((await readReportError(error)) || "Failed to get report.");
    } finally {
      setIsDealReportLoading(false);
    }
  };

  useEffect(() => {
    setSearchParams(`filter=${value?.toString()}`);
  }, [value]);

  useEffect(() => {
    if (searchParams?.get("filter")?.trim()) {
      setValue(searchParams?.get("filter")?.trim());
    }
  }, []);

  return (
    <>
      <div className="flex justify-between flex-wrap gap-5 items-center">
        <div className="flex items-center flex-wrap gap-3">
          <Segmented
            options={["Pending", "Approved", "Declined"]}
            value={value}
            setValue={setValue}
          />
          {value === "Approved" && (
            <>
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleOpenDealReportModal}
                  className="bg-primary text-sm text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90 duration-300"
                >
                  Get Deal Commission Report
                </button>
              )}
              <button
                type="button"
                onClick={handleOpenReportModal}
                className="bg-primary text-sm text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90 duration-300"
              >
                Get Hierarchy Commission Report
              </button>
            </>
          )}
        </div>
        <div className="flex items-center flex-wrap gap-3">
          <Input
            size="large"
            style={{ width: 220 }}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => setSearchText(search)}
            prefix={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                  clipRule="evenodd"
                />
              </svg>
            }
            suffix={
              search && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => {
                    setSearchText("");
                    setSearch("");
                  }}
                  viewBox="0 0 24 24"
                  fill="#bebebe"
                  className="size-5 cursor-pointer duration-300 hover:fill-[#969595]"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                    clipRule="evenodd"
                  />
                </svg>
              )
            }
          />
          <div>
            <ExportHandler exportHandler={exportHandler} />
          </div>
        </div>
      </div>
      {value === "Pending" ? <IbPending dates={searchText} /> : ""}
      {value === "Approved" ? <IbAccepted dates={searchText} /> : ""}
      {value === "Declined" ? <IbReject dates={searchText} /> : ""}

      <ModalComponent
        isOpen={isReportModalOpen}
        setIsOpen={setIsReportModalOpen}
        title="Get Hierarchy Commission Report"
        width={560}
      >
        <Form
          form={reportForm}
          layout="vertical"
          onFinish={handleGetReport}
          className="w-full"
        >
          <div className="ant-select-selector-white">
            <Form.Item
              name="email"
              label="IB Email"
              rules={[{ required: true, message: "Please select IB email." }]}
            >
              <Select
                size="large"
                showSearch
                allowClear
                loading={isIbOptionsLoading}
                placeholder="Select IB email"
                options={ibEmailOptions}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(String(input).toLowerCase())
                }
              />
            </Form.Item>
          </div>

          <div style={{ marginTop: 24 }}>
            <Form.Item
              name="dateRange"
              label="Date Range ( Optional )"
              // rules={[{ required: true, message: "Please select date range." }]}
            >
              <RangePicker className="w-full py-2" format="DD-MM-YYYY" />
            </Form.Item>
          </div>

          <div className="flex justify-end items-center gap-3 mt-6">
            <button
              type="button"
              onClick={handleCloseReportModal}
              disabled={isReportLoading}
              className="bg-secondary text-sm text-white font-semibold rounded-lg px-5 py-2"
            >
              Cancel
            </button>
            <LoadableButton
              type="submit"
              className="bg-primary text-sm text-white font-semibold rounded-lg px-5 py-2"
              lable="Get Report"
              isLoading={isReportLoading}
              loadingLable="Getting report..."
            />
          </div>
        </Form>
      </ModalComponent>

      <ModalComponent
        isOpen={isDealReportModalOpen}
        setIsOpen={setIsDealReportModalOpen}
        title="Get Deal Commission Report"
        width={560}
      >
        <Form
          form={dealReportForm}
          layout="vertical"
          onFinish={handleGetDealReport}
          className="w-full"
        >
          <div className="ant-select-selector-white">
            <Form.Item
              name="ibEmail"
              label="IB Email"
              rules={[{ required: true, message: "Please select IB email." }]}
            >
              <Select
                size="large"
                showSearch
                allowClear
                loading={isIbOptionsLoading}
                placeholder="Select IB email"
                options={ibEmailOptions}
                optionFilterProp="label"
                onChange={handleIbEmailChange}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(String(input).toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.ibEmail !== next.ibEmail}
            >
              {() => {
                const ibEmail = dealReportForm.getFieldValue("ibEmail");
                const parentHelp = !ibEmail
                  ? "Select an IB first. Only IBs above that IB will be listed."
                  : selectedIbIsMaster
                    ? "This IB is already at the top. The report will use them as the master IB."
                    : "Only IBs above the selected IB are shown. Leave empty to go up to the master IB.";

                return (
                  <Form.Item
                    name="parentEmail"
                    label="Parent IB Email (Optional)"
                    extra={parentHelp}
                  >
                    <Select
                      size="large"
                      showSearch
                      allowClear
                      disabled={!ibEmail}
                      loading={isParentOptionsLoading}
                      placeholder={
                        !ibEmail
                          ? "Select an IB first"
                          : selectedIbIsMaster
                            ? "No parent above this IB"
                            : "Select parent IB email"
                      }
                      options={parentIbOptions}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        String(option?.label ?? "")
                          .toLowerCase()
                          .includes(String(input).toLowerCase())
                      }
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>

          <div className="flex justify-end items-center gap-3 mt-6">
            <button
              type="button"
              onClick={handleCloseDealReportModal}
              disabled={isDealReportLoading}
              className="bg-secondary text-sm text-white font-semibold rounded-lg px-5 py-2"
            >
              Cancel
            </button>
            <LoadableButton
              type="submit"
              className="bg-primary text-sm text-white font-semibold rounded-lg px-5 py-2"
              lable="Get Report"
              isLoading={isDealReportLoading}
              loadingLable="Getting report..."
            />
          </div>
        </Form>
      </ModalComponent>
    </>
  );
}
